import { useEffect, useRef, useState } from 'react'

import ToastService from 'services/toast.service'
import CustomCardService from 'services/customCards.service'

import { convertCustomCardsToCardType, useCardTypeAsCustomCard } from 'hooks/customCard'

import { CardType, defaultCard } from 'types/deck'
import { CustomCard } from 'services/apiTypes/customCards.types'

import PhatInput from 'components/formElements/PhatInput'
import Checkbox from 'components/formElements/Checkbox'
import TextArea from 'components/formElements/TextArea'
import PhatButton from 'components/formElements/PhatButton'
import ImageManipulationOverlay from 'components/customCards/ImageManipulationOverlay'
import QuantityInput from 'components/card/cardForm/quantityInput'

import styles from './customCardEditOverlay.module.scss'

type Props = {
  card: CardType | null
  onCardEdited: (updatedCard: CustomCard) => void
  onDisplayCardEdited?: (updatedCard: CardType) => void
  saveableByDefault?: boolean
}

const CustomCardEditForm = ({ card, onCardEdited, onDisplayCardEdited, saveableByDefault = false }: Props) => {
  const ignoreUnsavedWarningFlag = useRef(false)

  // prettier-ignore
  const [imageManipulationOverlayOpen, setImageManipulationOverlayOpen] = useState<'frontImageUrl' | 'backImageUrl' | null>(null)
  const [loading, setLoading] = useState(false)

  const [hasMadeChanges, setHasMadeChanges] = useState(saveableByDefault)

  const customCard = useCardTypeAsCustomCard(card || defaultCard)
  const [editedCustomCard, setEditedCustomCard] = useState(customCard)

  const handleSetEditedCustomCard = (updates: Partial<CustomCard>) => {
    // Makes TS happy since this can be null when the overlay is closed
    if (!editedCustomCard) return

    setHasMadeChanges(true)
    setEditedCustomCard({ ...editedCustomCard, ...updates })

    if (onDisplayCardEdited) {
      const [displayCard] = convertCustomCardsToCardType({ ...editedCustomCard, ...updates }, false, 0, true)

      onDisplayCardEdited(displayCard)
    }
  }

  const handleSaveCustomCard = (e: React.FormEvent) => {
    e.preventDefault()

    if (!editedCustomCard) return

    if (!editedCustomCard.frontName)
      return ToastService.create('Custom cards require at least a front name', 'Custom Card service', 'warning')
    if (!editedCustomCard.frontTypes)
      return ToastService.create('Custom cards require at least one card type', 'Custom Card service', 'warning')

    setLoading(true)

    const request = editedCustomCard.id
      ? CustomCardService.update(editedCustomCard)
      : CustomCardService.create(editedCustomCard)

    return request
      .then(customCard => {
        ignoreUnsavedWarningFlag.current = true

        onCardEdited(customCard)
        handleCleanup(true)

        ToastService.create('Updated custom card', 'Custom Card Service', 'success')
      })
      .catch(err => ToastService.create('Unable to create/update Custom Card', 'Custom Card Service', 'error'))
      .finally(() => setLoading(false))
  }

  const handleCleanup = (skipHasEditsCheck = false) => {
    if (hasMadeChanges && !skipHasEditsCheck)
      ToastService.create(
        'Custom Card changes were not saved. If this is intentional you can ignore this message',
        'Custom Cards',
        'warning',
      )

    setHasMadeChanges(false)
  }

  useEffect(() => {
    return () => {
      if (!hasMadeChanges) return

      // This is set to true when we create/update a card. We can set this override back to the default state when we leave after a save
      // Must be a ref due to not being able to update state quickly enough after calling onCardEdited
      if (ignoreUnsavedWarningFlag.current) {
        ignoreUnsavedWarningFlag.current = false

        return
      }

      ToastService.create('Discarding unsaved custom card changes', 'Custom card edit form', 'warning')
    }
  }, [hasMadeChanges])

  return (
    <>
      <form onSubmit={handleSaveCustomCard} className={styles.form}>
        <h3>Front of the card</h3>
        <div className={styles.tripleRow}>
          <PhatInput
            header="Card name*"
            selectOnFocus
            value={editedCustomCard?.frontName}
            placeholder="Oopsie, Walking Calamity"
            onChange={frontName => handleSetEditedCustomCard({ frontName })}
          />

          <PhatInput
            header="Casting cost"
            placeholder="{1}{U}{U}"
            selectOnFocus
            value={editedCustomCard?.frontManaCost || ''}
            onChange={frontManaCost => handleSetEditedCustomCard({ frontManaCost })}
          />

          <QuantityInput
            label="Mana value"
            className={styles.manaValue}
            quantity={editedCustomCard?.cmc || 0}
            onQuantityUpdate={cmc => handleSetEditedCustomCard({ cmc })}
          />
        </div>

        <div className={styles.tripleRow}>
          <PhatInput
            header="Supertypes"
            placeholder="Legendary"
            selectOnFocus
            value={editedCustomCard?.frontSuperTypes || ''}
            onChange={frontSuperTypes => handleSetEditedCustomCard({ frontSuperTypes })}
          />
          <PhatInput
            header="Types*"
            placeholder="Artifact Creature"
            selectOnFocus
            value={editedCustomCard?.frontTypes || ''}
            onChange={frontTypes => handleSetEditedCustomCard({ frontTypes })}
          />
          <PhatInput
            header="Subtypes"
            placeholder="Robot Dog"
            value={editedCustomCard?.frontSubTypes || ''}
            onChange={frontSubTypes => handleSetEditedCustomCard({ frontSubTypes })}
          />
        </div>

        <div className={styles.tripleRow}>
          <PhatInput
            header="Power"
            placeholder="0"
            selectOnFocus
            value={editedCustomCard?.frontPower || ''}
            onChange={frontPower => handleSetEditedCustomCard({ frontPower })}
          />

          <PhatInput
            header="Toughness"
            placeholder="8"
            selectOnFocus
            value={editedCustomCard?.frontToughness || ''}
            onChange={frontToughness => handleSetEditedCustomCard({ frontToughness })}
          />

          <PhatInput
            header="Loyalty"
            placeholder=""
            selectOnFocus
            value={editedCustomCard?.frontLoyalty || ''}
            onChange={frontLoyalty => handleSetEditedCustomCard({ frontLoyalty })}
          />
        </div>

        <TextArea
          header="Front text"
          value={editedCustomCard?.frontText || ''}
          onChange={frontText => handleSetEditedCustomCard({ frontText })}
          placeholder={`Haste\n\n{T}: Add {C}{C}{C}{C}.\n\nWhenever this card becomes tapped, exile it and return it to the battlefield transformed at the beginning of the next end step.`}
        />

        <div className={styles.imageUploadRow}>
          <PhatButton
            icon="close"
            color="red"
            disabled={!editedCustomCard.frontImageUrl}
            className={styles.removeImageUrl}
            onClick={() => handleSetEditedCustomCard({ frontImageUrl: null })}
          />

          {editedCustomCard.frontImageUrl && (
            <label htmlFor="uploadTrigger" className={styles.imageUrl}>
              Custom image URL selected
            </label>
          )}

          {!editedCustomCard.frontImageUrl && (
            <label htmlFor="uploadTrigger" className={`${styles.imageUrl} ${styles.noImageSelected}`}>
              No image uploaded
            </label>
          )}

          <PhatButton
            id="uploadTrigger"
            className={styles.uploadTrigger}
            color="blue"
            type="button"
            icon="upload"
            onClick={() => setImageManipulationOverlayOpen('frontImageUrl')}>
            Upload image
          </PhatButton>
        </div>

        <div className={styles.tripleRow}>
          <PhatInput
            header="Artist name"
            placeholder="Someone Artist"
            selectOnFocus
            value={editedCustomCard?.frontArtistName || ''}
            onChange={frontArtistName => handleSetEditedCustomCard({ frontArtistName })}
          />

          <PhatInput
            header="Edition code"
            placeholder="lol"
            selectOnFocus
            value={editedCustomCard?.setCode || ''}
            onChange={setCode => handleSetEditedCustomCard({ setCode })}
          />

          <PhatInput
            header="Collector number"
            placeholder="777"
            selectOnFocus
            value={editedCustomCard?.collectorNumber || ''}
            onChange={collectorNumber => handleSetEditedCustomCard({ collectorNumber })}
          />
        </div>

        <Checkbox
          label="Double faced card"
          checked={editedCustomCard?.hasBack}
          onChange={() => handleSetEditedCustomCard({ hasBack: !editedCustomCard?.hasBack })}
        />
        {editedCustomCard?.hasBack && (
          <>
            <h3>Back of the card</h3>

            <div className={styles.row}>
              <PhatInput
                header="Card name"
                selectOnFocus
                value={editedCustomCard?.backName || ''}
                placeholder="An accident waiting to happen"
                onChange={backName => handleSetEditedCustomCard({ backName })}
              />

              <PhatInput
                header="Casting cost"
                placeholder=""
                selectOnFocus
                value={editedCustomCard?.backManaCost || ''}
                onChange={backManaCost => handleSetEditedCustomCard({ backManaCost })}
              />
            </div>

            <div className={styles.tripleRow}>
              <PhatInput
                header="Supertypes"
                placeholder="Legendary"
                selectOnFocus
                value={editedCustomCard?.backSuperTypes || ''}
                onChange={backSuperTypes => handleSetEditedCustomCard({ backSuperTypes })}
              />
              <PhatInput
                header="Types"
                placeholder="Enchantment"
                selectOnFocus
                value={editedCustomCard?.backTypes || ''}
                onChange={backTypes => handleSetEditedCustomCard({ backTypes })}
              />
              <PhatInput
                header="Subtypes"
                placeholder=""
                selectOnFocus
                value={editedCustomCard?.backSubTypes || ''}
                onChange={backSubTypes => handleSetEditedCustomCard({ backSubTypes })}
              />
            </div>

            <div className={styles.tripleRow}>
              <PhatInput
                header="Power"
                placeholder=""
                selectOnFocus
                value={editedCustomCard?.backPower || ''}
                onChange={backPower => handleSetEditedCustomCard({ backPower })}
              />

              <PhatInput
                header="Toughness"
                placeholder=""
                selectOnFocus
                value={editedCustomCard?.backToughness || ''}
                onChange={backToughness => handleSetEditedCustomCard({ backToughness })}
              />

              <PhatInput
                header="Loyalty"
                placeholder=""
                selectOnFocus
                value={editedCustomCard?.backLoyalty || ''}
                onChange={backLoyalty => handleSetEditedCustomCard({ backLoyalty })}
              />
            </div>

            <TextArea
              header="Text"
              value={editedCustomCard?.backText || ''}
              onChange={backText => handleSetEditedCustomCard({ backText })}
              placeholder={`Indestructible\n\nAn accident waiting to happen cannot be sacrificed.\n\nIf An accident waiting to happen becomes tapped, its controller loses the game.`}
            />

            <div className={styles.imageUploadRow}>
              <PhatButton
                icon="close"
                color="red"
                disabled={!editedCustomCard.backImageUrl}
                className={styles.removeImageUrl}
                onClick={() => handleSetEditedCustomCard({ backImageUrl: null })}
              />

              {editedCustomCard.backImageUrl && (
                <label htmlFor="uploadTrigger" className={styles.imageUrl}>
                  Custom image URL selected
                </label>
              )}

              {!editedCustomCard.backImageUrl && (
                <label htmlFor="uploadTrigger" className={`${styles.imageUrl} ${styles.noImageSelected}`}>
                  No image uploaded
                </label>
              )}

              <PhatButton
                id="uploadTrigger"
                className={styles.uploadTrigger}
                color="blue"
                type="button"
                icon="upload"
                onClick={() => setImageManipulationOverlayOpen('backImageUrl')}>
                Upload image
              </PhatButton>
            </div>

            <div className={styles.tripleRow}>
              <PhatInput
                header="Artist name"
                selectOnFocus
                placeholder="Someone Artist"
                value={editedCustomCard?.backArtistName || ''}
                onChange={backArtistName => handleSetEditedCustomCard({ backArtistName })}
              />
            </div>
          </>
        )}

        <div className={styles.controls}>
          <PhatButton disabled={loading || !hasMadeChanges} color="green" type="submit" icon="save outline">
            {editedCustomCard?.id ? 'Update' : 'Create'} Custom Card
          </PhatButton>
        </div>
      </form>

      <ImageManipulationOverlay
        open={!!imageManipulationOverlayOpen}
        onImageUploaded={imageUrl => {
          if (imageManipulationOverlayOpen) handleSetEditedCustomCard({ [imageManipulationOverlayOpen]: imageUrl })
        }}
        onClose={() => setImageManipulationOverlayOpen(null)}
      />
    </>
  )
}

export default CustomCardEditForm
